import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Users, CalendarDays, CheckCircle, Clock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/admin/StatusBadge";

export default function AdvisorDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/login", { replace: true });
      else if (!["advisor", "admin"].includes(user.role)) navigate("/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user || !["advisor", "admin"].includes(user.role)) return;
    api.get("/advisors/me/dashboard")
      .then(r => setData(r.data))
      .catch(e => toast.error(errMsg(e)))
      .finally(() => setLoadingData(false));
  }, [user]);

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center bg-[hsl(var(--zt-beige))]"><Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} /></div>;
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--zt-beige))] pb-24">
      <div className="bg-[hsl(var(--zt-teal))] text-white relative overflow-hidden">
        <div aria-hidden className="absolute -top-40 -right-40 w-[450px] h-[450px] rounded-full opacity-[0.15] blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, #4F63F0 0%, transparent 60%)" }} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-24 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="transition-transform hover:scale-[1.02]">
              <img src="/zenstrust-logo.png" alt="Zenstrust" className="h-14 w-auto rounded-md object-contain" />
            </button>
            <span className="hidden sm:inline font-heading uppercase tracking-[0.25em] text-[10px] text-[hsl(var(--zt-gold))] font-bold border-l border-white/15 pl-4">
              Advisor Portal
            </span>
          </div>
          <Button variant="ghost" onClick={async () => { await logout(); navigate("/"); }} className="text-white hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-5 rounded-full border border-white/10">
            <LogOut size={15} className="mr-2" /> Log out
          </Button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12 space-y-10">
        <div>
          <span className="font-heading uppercase tracking-[0.32em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">Welcome back,</span>
          <h1 className="font-display text-4xl lg:text-5xl text-[hsl(var(--zt-teal))] font-bold mt-2">
            {user.name?.split(" ")[0] || "Advisor"}<span className="text-[hsl(var(--zt-gold))]">.</span>
          </h1>
        </div>

        {loadingData ? (
          <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} /></div>
        ) : data ? (
          <div className="space-y-8">
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Assigned Clients",    value: data.stats?.assigned_clients ?? 0,    icon: Users,       color: "text-[hsl(var(--zt-teal))]" },
                { label: "Today's Meetings",    value: data.stats?.todays_meetings ?? 0,      icon: CalendarDays, color: "text-[hsl(var(--zt-gold))]" },
                { label: "Upcoming Meetings",   value: data.stats?.upcoming_meetings ?? 0,    icon: Clock,        color: "text-indigo-500" },
                { label: "Completed Meetings",  value: data.stats?.completed_meetings ?? 0,   icon: CheckCircle,  color: "text-emerald-500" },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-5 shadow-sm flex items-center gap-4">
                  <s.icon size={22} className={s.color} />
                  <div>
                    <p className="font-display text-2xl text-[hsl(var(--zt-teal))] font-bold leading-none">{s.value}</p>
                    <p className="font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/50 mt-1">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Today's appointments + consultations */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Section title={`Today's Appointments (${data.todays_appointments?.length ?? 0})`}>
                {!data.todays_appointments?.length ? (
                  <Empty text="No meetings today" />
                ) : data.todays_appointments.map(a => (
                  <AptCard key={a.appointment_id} apt={a} />
                ))}
              </Section>

              <Section title={`Upcoming (${data.upcoming_appointments?.length ?? 0})`}>
                {!data.upcoming_appointments?.length ? (
                  <Empty text="No upcoming meetings" />
                ) : data.upcoming_appointments.map(a => (
                  <AptCard key={a.appointment_id} apt={a} />
                ))}
              </Section>
            </div>

            {/* Consultations */}
            <Section title={`Assigned Consultations (${data.consultations?.length ?? 0})`}>
              {!data.consultations?.length ? (
                <Empty text="No assigned consultations" />
              ) : (
                <div className="grid gap-3">
                  {data.consultations.map(c => (
                    <div key={c.consultation_id} className="flex items-center justify-between px-4 py-3 bg-[hsl(var(--zt-beige))]/50 rounded-xl">
                      <div>
                        <p className="text-sm font-semibold text-[hsl(var(--zt-teal))]">{c.subject}</p>
                        <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{c.user_name} · {fmtDate(c.created_at)}</p>
                      </div>
                      <StatusBadge status={c.status} />
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40">
        <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">{title}</h3>
      </div>
      <div className="p-4 space-y-2">{children}</div>
    </div>
  );
}

function AptCard({ apt }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-[hsl(var(--zt-beige))]/50 rounded-xl">
      <div className="flex-1">
        <p className="text-sm font-semibold text-[hsl(var(--zt-teal))]">{apt.user_name || apt.user_email}</p>
        <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{fmtDateTime(apt.scheduled_at)} · {apt.meeting_mode}</p>
      </div>
      {apt.meeting_link && (
        <a href={apt.meeting_link} target="_blank" rel="noopener noreferrer"
          className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-gold))] hover:underline">Join</a>
      )}
      <StatusBadge status={apt.status} />
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-center text-[hsl(var(--zt-charcoal))]/40 py-6">{text}</p>;
}

function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
function fmtDateTime(d) {
  if (!d) return "";
  return new Date(d).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
